/**
 * ComeFunziona — sezione "Cos'è Pronto, la nostra filosofia,
 * tutto quello che fa".
 *
 * Posizionata subito dopo Hero per chi atterra per la prima volta.
 *
 * iter v31 r50 — User: «a destra del logo metterei "Come funziona" che
 * spiega cosa facciamo e la filosofia nostra». Voce nav del HeaderV2
 * punta qui via anchor `#come-funziona`.
 *
 * REGOLE:
 *  - Solo cose che esistono nel codice (no invenzioni).
 *  - Niente "AI" o "intelligenza artificiale" come parola.
 *  - Niente "innovativo", "rivoluzionario", "studio".
 *  - Tono umile, professionale, italiano vero.
 */
import React from "react";
import {
  MicrophoneStage,
  Image as ImageIcon,
  InstagramLogo,
  Browsers,
  FileXls,
  Calendar,
  WhatsappLogo,
  AddressBook,
  ChatCircleDots,
  Calculator,
  House,
} from "@phosphor-icons/react";

const PHILOSOPHY = [
  {
    title: "L'agente al centro, non sostituito.",
    body:
      "Pronto fa il lavoro noioso: scrivere, ritoccare, impaginare. Il cliente, la trattativa e la firma restano con te.",
  },
  {
    title: "Tu decidi chi vede.",
    body:
      "La tua vetrina e gli annunci che generi non sono pubblici. Si vedono solo da chi riceve il link che invii tu. Revocabile sempre.",
  },
  {
    title: "Solo quello che fa davvero.",
    body:
      "Niente stelle finte, niente \"+500 agenti\" inventati, niente feature in roadmap spacciate per pronte. Se non lo trovi scritto, non c'è.",
  },
  {
    title: "Italiana, per come lavora un'agenzia italiana.",
    body:
      "Idealista, Immobiliare.it, gestionali italiani, festività italiane, fattura elettronica. Italiano vero, no traduzioni meccaniche.",
  },
];

const CAPABILITIES = [
  { icon: MicrophoneStage, label: "Sopralluogo vocale", sub: "annuncio + vetrina" },
  { icon: Calculator, label: "Stima cliente online", sub: "anonima · sblocca lasciandoti il contatto" },
  { icon: ImageIcon, label: "Foto migliorate", sub: "arreda, restyling, svuota, pianta" },
  { icon: House, label: "Pagina annuncio brandizzata", sub: "una mini-vetrina online col tuo logo e colori" },
  { icon: InstagramLogo, label: "Pacchetto social", sub: "post, story, carosello" },
  { icon: Browsers, label: "Estensione Chrome", sub: "Idealista, Immobiliare.it" },
  { icon: FileXls, label: "Import XML", sub: "dal gestionale dell'agenzia" },
  { icon: Calendar, label: "Agenda vocale", sub: "sync Google e Apple, push 30 min prima" },
  { icon: WhatsappLogo, label: "Messaggi WhatsApp", sub: "auguri, festività, nuovi annunci, open house" },
  { icon: AddressBook, label: "Contatti", sub: "CRM e rubrica importata" },
  { icon: ChatCircleDots, label: "Assistente Pronto", sub: "chiedi, ti risponde" },
];

export default function ComeFunziona() {
  return (
    <section
      id="come-funziona"
      className="py-14 sm:py-20 bg-[#FAFAF7] relative overflow-hidden"
      data-testid="feature-come-funziona"
    >
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 lg:px-10">
        {/* HEADER conciso */}
        <div className="max-w-3xl">
          <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E]">
            Cos&apos;è Pronto
          </span>
          <h2
            className="font-editorial text-[28px] sm:text-[40px] lg:text-[48px] mt-3 leading-[1.05] tracking-tighter text-[#1C1C1A]"
            data-testid="come-funziona-title"
          >
            L&apos;app degli agenti.
            <br />
            <span className="italic text-[#7C8B6E]">Tu fai il sopralluogo, lei prepara il resto.</span>
          </h2>
          <p className="mt-4 text-[14.5px] sm:text-[15.5px] text-[#3F3D38] leading-relaxed">
            Apri Pronto, registri una nota vocale girando per le stanze. Da quel
            vocale escono annuncio scritto, foto pronte e social con i campi
            gia&apos; compilati per Idealista e Immobiliare.it. Le foto le migliori
            tu con un tocco quando le vuoi. Quando un cliente chiede «quanto vale
            casa mia?», gli mandi il tuo link di stima: lui risponde a 6 domande,
            tu ricevi il contatto in rubrica.
          </p>
        </div>

        {/* FILOSOFIA — 4 punti compatti */}
        <div className="mt-10 sm:mt-12">
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E] mb-4">
            La nostra filosofia
          </div>
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
            data-testid="come-funziona-philosophy"
          >
            {PHILOSOPHY.map((p, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-[#E5E7E0] p-5"
                data-testid={`philosophy-${i + 1}`}
              >
                <div className="font-editorial text-[17px] sm:text-[18px] text-[#1C1C1A] leading-tight tracking-tight">
                  {p.title}
                </div>
                <p className="mt-2 text-[13px] text-[#3F3D38] leading-relaxed">
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CAPABILITIES — recap rapido */}
        <div className="mt-10 sm:mt-12">
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E] mb-4">
            Tutto quello che fa, in un colpo d&apos;occhio
          </div>
          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
            data-testid="come-funziona-capabilities"
          >
            {CAPABILITIES.map((c, i) => {
              const Icon = c.icon;
              return (
                <div
                  key={i}
                  className="flex items-start gap-3 bg-white rounded-xl border border-[#E5E7E0] px-3.5 py-3 hover:border-[#7C8B6E]/40 transition-colors"
                  data-testid={`capability-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-[#F5F2EB] flex items-center justify-center shrink-0">
                    <Icon size={18} weight="regular" className="text-[#5A6A4D]" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[12.5px] font-semibold text-[#1C1C1A] leading-tight">
                      {c.label}
                    </div>
                    <div className="text-[11px] text-[#6B6A65] leading-tight mt-0.5">
                      {c.sub}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
